import { Box, Loader } from "@mantine/core";
import { GraphQL, Rest } from "@rpi/openapi-api";
import { Flex, RpiText } from "@rpi/openapi-core";
import React from "react";
import { formatCurrency } from "../../utils/misc.util";
import { LANDING_PAGE_MAX_WIDTH } from "./IndexView";

interface PricingPageProps {
  products?: Rest.PriceListInfo["products"];
}

// Cover type segment (index 1 in underscore-delimited SKU).
// SCIW = softcover imagewrap, HCIW = hardcover imagewrap.
const COVER_TYPE_ORDER = new Map<string, number>([
  ["SCIW", 0],
  ["HCIW", 1],
]);

// Laminate finish segment. GL = gloss, MT = matte.
// Appears as a standalone segment after the dimension/weight parts of the SKU.
const LAMINATE_ORDER = new Map<string, number>([
  ["GL", 0],
  ["MT", 1],
]);

// Rounds up to the nearest 0.5 to normalize SKU dimensions to nominal book sizes.
// SCIW and HCIW variants of the same book have slightly different trim dimensions
// (e.g., SCIW 8x8 = 8.000x8.000IN, HCIW 8x8 = 7.750x7.750IN) but should group
// together. Rounding 7.750 → 8 and 8.000 → 8 produces matching nominal values.
function roundToNominal(value: number): number {
  return Math.ceil(value * 2) / 2;
}

// Extracts sort-relevant fields from a SKU string.
//
// Expected SKU format: "BKPH_HCIW_STD_7.750x7.750IN_80C_GL_40_100T_GLS_44"
//   - parts[1]: cover type (SCIW | HCIW)
//   - dimension segment: matches pattern \d+\.\d+x\d+\.\d+IN (e.g., "7.750x7.750IN")
//   - laminate segment: standalone "GL" or "MT" token
//
// Edge cases:
//   - SKUs without a recognizable dimension segment (e.g., old-format or test SKUs)
//     default to Infinity for area/width, pushing them to the bottom of the sort.
//   - SKUs with an unrecognized cover type (not SCIW/HCIW) default to order 99.
//   - SKUs without a GL/MT laminate segment default to order 99.
//   - The laminate scan uses reduce() across all parts, so the last matching segment
//     wins if multiple GL/MT tokens exist (unlikely in practice).
function parseSkuSortKey(sku: string) {
  const parts = sku.split("_");
  const coverType = COVER_TYPE_ORDER.get(parts[1]) ?? 99;

  const dimPart = parts.find((p) => /^\d+\.\d+x\d+\.\d+IN$/i.test(p));
  let area = Infinity;
  let width = Infinity;
  if (dimPart) {
    const [w, h] = dimPart.replace(/IN$/i, "").split("x").map(Number);
    const nominalW = roundToNominal(w);
    const nominalH = roundToNominal(h);
    area = nominalW * nominalH;
    width = nominalW;
  }

  const laminate = parts.reduce((found, p) => {
    if (LAMINATE_ORDER.has(p)) return LAMINATE_ORDER.get(p)!;
    return found;
  }, 99);

  return { area, width, coverType, laminate };
}

// Sorts products for display in the pricing table.
//
// Sort order:
//   1. Nominal area (smallest book first) — groups all variants of the same
//      nominal size together even though SCIW/HCIW have different trim dimensions.
//   2. Width (narrower first) — distinguishes portrait from landscape when two
//      sizes have the same area (e.g., 8.5×11 vs 11×8.5 both have area 93.5).
//   3. Cover type — SCIW (softcover imagewrap) before HCIW (hardcover imagewrap).
//   4. Laminate — GL (gloss) before MT (matte).
//
// Products with unparseable SKUs sort to the bottom (Infinity defaults).
// Does not mutate the input array.
export function sortProducts(products: Rest.PriceListInfo["products"]): Rest.PriceListInfo["products"] {
  return [...products].sort((a, b) => {
    const skuA = parseSkuSortKey(a.openapiSku);
    const skuB = parseSkuSortKey(b.openapiSku);

    if (skuA.area !== skuB.area) return skuA.area - skuB.area;
    if (skuA.width !== skuB.width) return skuA.width - skuB.width;
    if (skuA.coverType !== skuB.coverType) return skuA.coverType - skuB.coverType;
    return skuA.laminate - skuB.laminate;
  });
}

export const PricingPage: React.FC<PricingPageProps> = ({ products }) => {
  const displayProducts = React.useMemo(() => {
    if (!products) return undefined;
    return sortProducts(products);
  }, [products]);

  return (
    <Flex.Column
      justify="center"
      align="center"
      fullWidth
      p={80}
      sx={(theme) => ({ background: theme.colors.calm[5] })}
    >
      <Flex.Column
        justify="center"
        align="center"
        fullWidth
        p={50}
        sx={(theme) => ({
          flex: 1,
          background: theme.colors.sky[5],
          borderRadius: 24,
          position: "relative",
          maxWidth: LANDING_PAGE_MAX_WIDTH,
        })}
      >
        <Flex.Column align="center" fullWidth gap={24} sx={{ maxWidth: 749 }}>
          <RpiText type="d2" weight="bold" color={(theme) => theme.colors.brand[5]} children="Pricing" />
          <Box
            component="table"
            sx={(theme) => ({
              width: "100%",
              tableLayout: "fixed",
              borderCollapse: "collapse",
              color: theme.colors.text[5],

              "& > thead": {
                "& > tr": {
                  height: 48,
                  borderBottom: `1px solid ${theme.colors.text[5]}`,

                  "& > th": {
                    ...theme.other.fontTypes.p2,
                    fontWeight: theme.other.fontWeights.bold,
                    textAlign: "left",
                    padding: "0px 30px",
                    verticalAlign: "top",
                  },
                },
              },
              "& > tbody": {
                "& > tr": {
                  height: 48,
                  borderBottom: `1px solid ${theme.colors.text[5]}`,
                  "&:last-of-type": {
                    borderBottom: "none",
                  },

                  "& > td": {
                    ...theme.other.fontTypes.p3,
                    fontWeight: theme.other.fontWeights.regular,
                    padding: "0px 30px",
                    position: "relative",
                  },
                },
              },
            })}
          >
            <thead>
              <tr>
                <th>Product</th>
                <th style={{ width: "170px" }}>
                  <Flex.Column align="flex-end">
                    <span>Cover</span>
                    <RpiText type="p3" weight="light" color="inherit" children="incl 20 page" />
                  </Flex.Column>
                </th>
                <th style={{ width: "170px" }}>
                  <Flex.Column align="flex-end">
                    <span>Extra pages</span>
                    <RpiText type="p3" weight="light" color="inherit" children="per side" />
                  </Flex.Column>
                </th>
              </tr>
            </thead>
            <tbody>
              {displayProducts?.map((product) => (
                <tr key={`product-row-${product.openapiSku}`}>
                  <td children={product.name} />
                  <Box
                    component="td"
                    sx={{ textAlign: "right" }}
                    children={formatCurrency(GraphQL.SupportedCurrency.Usd, product.basePriceUsd)}
                  />
                  <Box
                    component="td"
                    sx={{ textAlign: "right" }}
                    children={formatCurrency(GraphQL.SupportedCurrency.Usd, product.pageCostUsd)}
                  />
                </tr>
              ))}

              {!displayProducts &&
                Array(9)
                  .fill(0)
                  .map((_, i) => (
                    <tr key={`loading-product-row-${i}`}>
                      <td children={<Loader variant="dots" />} />
                      <td children={<Loader variant="dots" />} />
                      <td children={<Loader variant="dots" />} />
                    </tr>
                  ))}
            </tbody>
          </Box>
          <RpiText
            type="p3"
            weight="regular"
            sx={{ lineHeight: "48px" }}
            color={(theme) => theme.colors.brand[5]}
            children="Prices are rounded up. It does not include taxes and shipping."
          />
        </Flex.Column>
      </Flex.Column>
    </Flex.Column>
  );
};
